import { faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useVirtualizer } from '@tanstack/react-virtual';
import Fuse from 'fuse.js';
import { parseAsString, useQueryState } from 'nuqs';
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTripContext } from '@/contexts/TripContext';
import { ChipButton } from '@/design-system/primitives/ChipButton';
import type { ItineraryItem } from '@/types/itinerary';
import { categoryDefinitions } from '@/utils/categories';
import { EmptyState } from './EmptyState';
import { ItineraryCard } from './ItineraryCard';

type SortBy = 'distance' | 'rating';

/** Below this list size, a normal map() render is fast enough that
 *  virtualization's overhead (absolute positioning, measureElement on
 *  each first paint) isn't worth the brief layout flash. 50 puts us
 *  well above any short result set + well below the 2000+ count where
 *  the un-virtualized list actually janks. */
const VIRTUALIZATION_THRESHOLD = 50;

export interface ItineraryListProps {
  items: Array<ItineraryItem>;
  isLoading: boolean;
  hasSearched: boolean;
}

// Skeleton mirrors the compact ItineraryCard's structure: 16/9 photo
// (was 5/2 / aspect-video before the card redesign), then p-3 content
// with a title row (chip + name), an editorial-summary line, a single
// compact meta row, and an action row. Heights/widths match the final
// card closely so the swap-in is visually stable.
const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-content-border overflow-hidden animate-pulse">
    <div className="w-full aspect-[16/9] bg-content-background" />
    <div className="p-3 space-y-1.5">
      {/* Title row: category chip + name */}
      <div className="flex items-start gap-2">
        <div className="h-4 w-14 bg-content-background rounded-full shrink-0 mt-0.5" />
        <div className="h-5 flex-1 bg-content-background rounded" />
      </div>
      {/* Editorial summary line — line-clamp-1 in the real card */}
      <div className="h-3.5 w-5/6 bg-content-background rounded" />
      {/* Meta line */}
      <div className="h-3.5 w-2/3 bg-content-background rounded" />
      {/* Action row */}
      <div className="flex gap-3 pt-0.5">
        <div className="h-3.5 w-16 bg-content-background rounded" />
        <div className="h-3.5 w-12 bg-content-background rounded" />
        <div className="h-3.5 w-20 bg-content-background rounded" />
      </div>
    </div>
  </div>
);

const ItineraryListImpl = ({ items, isLoading, hasSearched }: ItineraryListProps) => {
  const [selectedItemId, setSelectedItemId] = useQueryState('item', parseAsString);
  const { trips, selectedTripId } = useTripContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [sortBy, setSortBy] = useState<SortBy>('distance');

  // Hoisted out of every ItineraryCard so trip mutations don't cascade through
  // the list. Memoed on date strings only — adding a place to the trip leaves
  // these unchanged and so leaves every card's memo intact.
  const tripDates = useMemo(() => {
    const trip = trips.find((t) => t.id === selectedTripId);
    if (!trip) return null;
    return { startDate: trip.startDate, endDate: trip.endDate };
  }, [trips, selectedTripId]);

  const handleSelectItem = useCallback(
    (id: string) => void setSelectedItemId((current) => (id === current ? null : id)),
    [setSelectedItemId],
  );

  // Building Fuse's index is O(n × keys) — expensive at ~1000 items. Key the
  // memo on a stable content signature rather than array identity so that
  // refetches returning identical data (e.g., debounced filter toggles that
  // hit the cache) don't trigger a full re-index.
  const itemsSignature =
    items.length === 0
      ? '0'
      : `${items.length}:${items[0]?.id ?? ''}:${items[items.length - 1]?.id ?? ''}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on content signature, not array identity — avoids rebuilding Fuse index when items array identity changes but content does not
  const fuse = useMemo(
    () =>
      new Fuse(items, {
        threshold: 0.35,
        ignoreLocation: true,
        keys: [
          { name: 'name', weight: 3 },
          { name: 'editorialSummary', weight: 1.5 },
          { name: 'categories.name', weight: 1 },
          { name: 'address', weight: 0.5 },
        ],
      }),
    [itemsSignature],
  );

  const searchedItems = useMemo(() => {
    const query = deferredSearchQuery.trim();
    if (query.length === 0) return items;
    // Fuse is O(items × keys) per search; with ~1k results + 4 keys + ignoreLocation,
    // a single-character fuzzy match scores nearly every item and visibly freezes the
    // main thread. Fall back to a cheap name-prefix filter until the user has typed
    // enough for fuzzy matching to be both faster and useful. Results are ranked:
    // exact match → startsWith → includes, so "Hen" surfaces "Hen of the Wood"
    // before "Chicken Coop".
    if (query.length < 3) {
      const needle = query.toLowerCase();
      const score = (name: string): number => {
        if (name === needle) return 0;
        if (name.startsWith(needle)) return 1;
        return 2;
      };
      return items
        .filter((item) => item.name.toLowerCase().includes(needle))
        .sort((a, b) => score(a.name.toLowerCase()) - score(b.name.toLowerCase()));
    }
    // Fuse returns results sorted by score (best match first); preserve that order.
    return fuse.search(query).map((r) => r.item);
  }, [fuse, items, deferredSearchQuery]);

  const displayedItems = useMemo(() => {
    // When the user is searching, preserve relevance ranking — sorting by
    // distance/rating would push exact name matches below random nearby places.
    if (deferredSearchQuery.trim().length > 0) return searchedItems;

    const sorted = [...searchedItems];
    if (sortBy === 'distance') {
      sorted.sort((a, b) => {
        if (a.distanceMiles == null && b.distanceMiles == null) return 0;
        if (a.distanceMiles == null) return 1;
        if (b.distanceMiles == null) return -1;
        return a.distanceMiles - b.distanceMiles;
      });
    } else {
      sorted.sort((a, b) => {
        if (a.rating == null && b.rating == null) return 0;
        if (a.rating == null) return 1;
        if (b.rating == null) return -1;
        return b.rating - a.rating;
      });
    }
    return sorted;
  }, [searchedItems, sortBy, deferredSearchQuery]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of searchedItems) {
      counts.set(item.appCategory, (counts.get(item.appCategory) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({
        category,
        count,
        label: categoryDefinitions[category as keyof typeof categoryDefinitions]?.label ?? category,
      }));
  }, [searchedItems]);

  // Virtualizer — at 2000+ places the un-virtualized list jank-stalls the
  // main thread (every render reconciles thousands of card subtrees plus
  // their FontAwesome icons + images, even though only ~6 are visible at a
  // time). @tanstack/react-virtual is a headless windower: we render only
  // the cards in (or near) the viewport, absolutely positioned inside a
  // tall spacer. Cards have variable height (photo vs no-photo, trip-day
  // hours vs none, multi-line names), so we let the virtualizer measure
  // each item on first paint via `measureElement` — `estimateSize` is just
  // a starting guess that the virtualizer refines.
  const shouldVirtualize = displayedItems.length >= VIRTUALIZATION_THRESHOLD;

  const virtualizer = useVirtualizer({
    // Setting count to 0 when below threshold keeps the hook stable
    // (rules of hooks) but lets us skip the windowed render path.
    count: shouldVirtualize ? displayedItems.length : 0,
    getScrollElement: () => scrollContainerRef.current,
    // 220px is the rough middle of "with photo, no editorial summary" and
    // "no photo, with summary + trip-day hours". The virtualizer self-
    // corrects on first paint, so this is only used before any item has
    // rendered (initial scroll height + a brief flash).
    estimateSize: () => 220,
    // +16px = the `mb-4` gap we apply per item (was `space-y-4` on the
    // un-virtualized list). Built into the measured size so the spacer
    // tracks correctly.
    measureElement: (el) => el.getBoundingClientRect().height,
    overscan: 5,
    getItemKey: (index) => displayedItems[index]?.id ?? index,
  });

  // Scroll selected card into view when set from map. For the
  // virtualized path, delegate to `scrollToIndex` (the item may not be
  // mounted yet). For the small-list path, the row is always mounted —
  // find it by data-id and scrollIntoView.
  useEffect(() => {
    if (!selectedItemId) return;
    const index = displayedItems.findIndex((item) => item.id === selectedItemId);
    if (index < 0) return;
    if (shouldVirtualize) {
      virtualizer.scrollToIndex(index, { align: 'center', behavior: 'smooth' });
    } else {
      scrollContainerRef.current
        ?.querySelector(`[data-place-id="${selectedItemId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedItemId, displayedItems, virtualizer, shouldVirtualize]);

  // Clear search when items change (new location searched).
  // biome-ignore lint/correctness/useExhaustiveDependencies: items is a prop — Biome incorrectly flags it as an outer-scope value
  useEffect(() => {
    setSearchQuery('');
  }, [items]);

  if (isLoading) {
    return (
      <div className="w-full flex flex-col overflow-hidden h-full">
        <div className="px-4 py-3 border-b border-content-divider">
          <div className="h-4 w-24 bg-content-background rounded animate-pulse" />
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {(['a', 'b', 'c', 'd'] as const).map((id) => (
            <SkeletonCard key={id} />
          ))}
        </div>
      </div>
    );
  }

  if (!hasSearched || items.length === 0) {
    return (
      // `overflow-y-auto` here so the empty-state's trip-list block (when
      // the user has saved trips) is scrollable into view on short
      // bottom panels. Without this the panel's fixed `h-full` clipped
      // the trip list off the bottom on medium and small screens.
      // Avoid `flex` here — flex-row would prevent the EmptyState's
      // intrinsic height from driving the scroll container's content
      // height, and `min-h-full` inside a flex-row child resolves to 0.
      <div className="w-full h-full overflow-y-auto">
        <EmptyState hasSearched={hasSearched && items.length === 0} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col overflow-hidden h-full">
      <div className="px-4 py-3 border-b border-content-divider shrink-0 space-y-2">
        {/* Search input */}
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter places…"
            className="w-full text-sm pl-3 pr-8 py-1.5 rounded-xl border border-content-border bg-content-focal placeholder:text-content-disabled text-content-text outline-none focus:border-secondary-main focus:ring-2 focus:ring-secondary-main/20 transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-content-disabled hover:text-content-subtle hover:bg-content-background transition-colors motion-safe:active:scale-90"
              aria-label="Clear search"
              title="Clear filter"
            >
              <FontAwesomeIcon icon={faXmark} style={{ width: 11, height: 11 }} />
            </button>
          )}
        </div>

        {/* Count + sort toggles. On mobile the bottom panel is short and
            the search bar + count alone are the high-signal bits — sort
            chips + the category breakdown rejoin from md+ where there's
            vertical room. */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-content-subtle font-medium">
            {searchQuery.trim()
              ? `${displayedItems.length} of ${items.length} ${items.length === 1 ? 'place' : 'places'}`
              : `${items.length} ${items.length === 1 ? 'place' : 'places'} found`}
          </p>
          <div className="hidden md:flex items-center gap-1 shrink-0">
            {(['distance', 'rating'] as const).map((option) => (
              <ChipButton
                key={option}
                selected={sortBy === option}
                onClick={() => setSortBy(option)}
              >
                {option === 'distance' ? 'Distance' : 'Rating'}
              </ChipButton>
            ))}
          </div>
        </div>
        {categoryCounts.length > 0 && (
          <p className="hidden md:block text-xs text-content-disabled">
            {categoryCounts.map(({ category, count, label }, i) => (
              <span key={category}>
                {i > 0 && (
                  <span className="mx-1 text-base leading-none text-content-disabled/50">·</span>
                )}
                {count} {label.toLowerCase()}
              </span>
            ))}
          </p>
        )}
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 pt-4 scroll-smooth">
        {displayedItems.length === 0 ? (
          <p className="text-sm text-content-subtle text-center py-8">
            No places match <span className="font-medium text-content-text">"{searchQuery}"</span>
          </p>
        ) : shouldVirtualize ? (
          // Virtualized window — only used when the list is large enough
          // to actually benefit (see `VIRTUALIZATION_THRESHOLD`). The
          // outer relative div is sized to the full content height so
          // the scrollbar reflects the total list even though only the
          // visible slice is mounted. Each item is absolutely positioned
          // at its measured offset; `pb-4` bakes the inter-card gap into
          // the measured height (the un-virtualized list used
          // `space-y-4` on the parent — that pattern doesn't work with
          // absolute children).
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const item = displayedItems[vItem.index];
              if (!item) return null;
              return (
                <div
                  key={item.id}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start}px)`,
                  }}
                  className="pb-4"
                >
                  <ItineraryCard
                    item={item}
                    isSelected={item.id === selectedItemId}
                    onSelect={handleSelectItem}
                    tripDates={tripDates}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          // Small lists fall through to a non-virtualized render. Avoids
          // jsdom-layout fights in tests and side-steps the brief
          // first-paint "estimate vs. measured" reflow virtualization
          // can show on lists too small to matter.
          <div className="space-y-4 pb-4">
            {displayedItems.map((item) => (
              <div key={item.id} data-place-id={item.id}>
                <ItineraryCard
                  item={item}
                  isSelected={item.id === selectedItemId}
                  onSelect={handleSelectItem}
                  tripDates={tripDates}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize the entire list. Children (cards) are already memoed; this skips
// re-rendering the list shell when its props are unchanged (e.g., AppInner
// rerenders due to trip mutation but `items` / `isLoading` are stable).
export const ItineraryList = memo(ItineraryListImpl);
